// Which dates this BROWSER has already confirmed, so one opponent does not quietly take two.
// See kumpula/TEAMS.md.
//
// WHAT THIS IS, PRECISELY: a soft lock. /teams/offer/ and /teams/accept/ are capability URLs with
// no login, so nothing identifies the people behind a claim - each tie has its own token, the menu
// lists them all, and the backend cannot tell that two confirmations came from the same club. The
// quota is `homematches + 1` exactly because every opponent must be offered at least two dates, so
// a team taking two has taken one from somebody else and the manager finds out by reading the
// season page.
//
// A cookie stops the ordinary case - a second click, or two people from the same club on one
// machine - and stops nothing else. Another device, another browser or cleared cookies all get
// through. The real guard is server-side; see TEAMS_BACKEND_CHANGES.md section 6. Do not let this
// file grow into something that pretends otherwise.
//
// THE LOCK IS PER TEAM, NOT PER LINK. teams-accept returns the team's whole candidate list in
// `alternatives`, so a claim recorded from one link is recognised on every other link of the same
// team - a newer offer link, or a per-date link the same opponent was also sent. The response
// carries no teamid, and this is what stands in for one.

export const CLAIM_COOKIE = 'teamsclaim'

// A visitor who plays at several venues accumulates entries; an entry is ~70 bytes against a 4 KB
// cookie limit, and 20 seasons' worth is far more than anyone has.
const MAX_CLAIMS = 20

/**
 * The stored claims, from whatever the cookie jar hands back.
 *
 * react-cookie parses a JSON-looking value for us, so this takes an array already - but a cookie
 * is visitor-writable and survives a release, so anything at all can arrive here. A malformed
 * one yields [] and the page behaves as if nothing was stored: a broken cookie must never block a
 * legitimate confirmation, which is the failure that would matter.
 */
export const readClaims = (raw) => {
  let list = raw
  if (typeof list === 'string') {
    try {
      list = JSON.parse(list)
    } catch (e) {
      return []
    }
  }
  if (!Array.isArray(list)) return []
  return list
    .filter((claim) => claim && typeof claim.token === 'string' && claim.token)
    .map((claim) => ({
      token: claim.token,
      date: typeof claim.date === 'string' ? claim.date : '',
      teamname: typeof claim.teamname === 'string' ? claim.teamname : '',
    }))
}

/**
 * The claim this visitor already holds against the team whose page they are on, or null.
 *
 * Matches on the URL's own token OR on any token in `alternatives`, which is what makes the lock
 * cover every link of one team. A claim made at another venue names tokens this team never lists,
 * so it cannot match - two teams offering the same visitor dates stay independent.
 */
export const claimFor = (claims, view, token) => {
  const list = readClaims(claims)
  if (!list.length) return null
  const tokens = {}
  if (token) tokens[token] = true
  const alternatives = view && Array.isArray(view.alternatives) ? view.alternatives : []
  alternatives.forEach((option) => {
    if (option && option.token) tokens[option.token] = true
  })
  return list.find((claim) => tokens[claim.token]) || null
}

/** The list to store after a confirmation: newest first, one entry per token, capped. */
export const withClaim = (claims, entry) => {
  if (!entry || !entry.token) return readClaims(claims)
  const fresh = {
    token: entry.token,
    date: entry.date || '',
    teamname: entry.teamname || '',
  }
  return [fresh]
    .concat(readClaims(claims).filter((claim) => claim.token !== fresh.token))
    .slice(0, MAX_CLAIMS)
}
